/*
 * Copyright (C) 2022 - present Instructure, Inc.
 *
 * This file is part of Canvas.
 *
 * Canvas is free software: you can redistribute it and/or modify it under
 * the terms of the GNU Affero General Public License as published by the Free
 * Software Foundation, version 3 of the License.
 *
 * Canvas is distributed in the hope that it will be useful, but WITHOUT ANY
 * WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 * A PARTICULAR PURPOSE. See the GNU Affero General Public License for more
 * details.
 *
 * You should have received a copy of the GNU Affero General Public License along
 * with this program. If not, see <http://www.gnu.org/licenses/>.
 */

import IntlPolyfills from '../IntlPolyfills'
import type {Capability} from '@instructure/updown'
import {oncePerPage} from '@instructure/updown'
import {registerTranslations} from '@canvas/i18n'
import {initI18next, isPilotLocale, loadI18nextTranslations} from '@canvas/i18next'
import {registerDatetimeBundle} from '@canvas/i18next/datetimeNamespace'
import doFetchApi from '@canvas/do-fetch-api-effect'
import fallbacks from 'translations/en.json'
import {captureException} from '@sentry/browser'

declare const ENV: {
  RAILS_ENVIRONMENT: 'development' | 'test' | 'production'
  LOCALE?: string
  readonly LOCALE_TRANSLATION_FILE: string
  readonly LOCALES: string[]
  [propName: string]: unknown
}

// Backfill ENV.LOCALE from ENV.LOCALES[0] if it does not exist
const LocaleBackfill: Capability = {
  up: () => {
    if (Array.isArray(ENV.LOCALES) && typeof ENV.LOCALE === 'undefined') {
      ENV.LOCALE = ENV.LOCALES[0]
      return {
        down: () => {
          delete ENV.LOCALE
        },
      }
    }
  },
  requires: [],
}

// Dynamic import creates a separate lazy chunk per locale — only the matching
// one is fetched at runtime.
async function importI18nextNamespaces(locale: string) {
  if (isPilotLocale(locale)) {
    // CFA-1207: this locale's namespaces are split the same way English's are.
    // Load only the eager remainder here -- lazy namespaces arrive on demand
    // via the generated import map (ui/shared/i18next/index.ts's backend) the
    // first time a feature bundle calls loadNamespaces() for one, exactly the
    // same trigger that already fires for every locale. Loading the
    // {locale}.json monolith as well here would download both the full locale
    // file AND the split chunks, defeating the point.
    try {
      return await import(
        /* webpackChunkName: "i18next-[request]" */
        `@instructure/translations/lib/canvas-lms/${locale}.eager.json`
      )
    } catch {
      // Missing/broken eager artifact -- fall back to the monolith rather than
      // leaving this locale with no translations.
      return await import(
        /* webpackChunkName: "i18next-[request]" */
        `@instructure/translations/lib/canvas-lms/${locale}.json`
      )
    }
  }

  return await import(
    /* webpackChunkName: "i18next-[request]" */
    `@instructure/translations/lib/canvas-lms/${locale}.json`
  )
}

// load the string translation file for this locale
const Translations: Capability = {
  up: oncePerPage('translations', async () => {
    const locale = ENV.LOCALE || navigator.language || 'en'

    if (ENV.RAILS_ENVIRONMENT === 'test' || locale === 'en') {
      // Sync i18next locale with Canvas locale
      await initI18next(locale)
      registerTranslations(locale, fallbacks)
      registerDatetimeBundle(locale, fallbacks)
    } else {
      // started together: the flat file is preloaded, the namespace bundle isn't
      const flatTranslations = doFetchApi({
        path: ENV.LOCALE_TRANSLATION_FILE,
        includeCSRFToken: false,
      })
      // caught here, not at the await: the fetch can reject first
      const i18nextNamespaces = importI18nextNamespaces(locale).catch(() => null)

      try {
        const {json} = await flatTranslations
        if (typeof json === 'object' && json !== null) {
          registerTranslations(locale, json)
          // Same flat locale file backs both the old @canvas/i18n system
          // (above) and the new i18next 'datetime' namespace (below) --
          // see ui/shared/i18next/datetimeNamespace.ts.
          registerDatetimeBundle(locale, json as Record<string, unknown>)
        }

        const i18nextModule = await i18nextNamespaces
        let i18nextLoaded = false
        if (i18nextModule) {
          try {
            loadI18nextTranslations(locale, i18nextModule.default || i18nextModule)
            i18nextLoaded = true
          } catch {
            // keep the flat translations registered above rather than
            // escalating to the English-fallback path below
          }
        }
        if (!i18nextLoaded) {
          console.warn(`[i18next] No translations for locale "${locale}" — using English fallback`)
        }

        // Sync i18next locale with Canvas locale -- only after any
        // translations for it are registered above, or i18next's own
        // init-time namespace sweep runs against an empty store and hits
        // the backend for every namespace at once (see initI18next's own
        // comment on this).
        await initI18next(locale)
      } catch {
        registerTranslations(locale, fallbacks)
        registerDatetimeBundle(locale, fallbacks)
        await initI18next(locale).catch(() => {})

        console.error(
          `CAUTION could not load translations for "${ENV.LOCALE}", falling back to US English`,
        )
        captureException(new Error(`Could not load translations for "${ENV.LOCALE}"`))
      }
    }
  }),
  requires: [LocaleBackfill],
}

const I18n: Capability = {
  up: () => {},
  requires: [IntlPolyfills, Translations],
}

export {I18n, Translations}
